import { computed } from 'vue';
import { useStore } from 'vuex';
import { loadTrueFit } from '@utils/loadTrueFit';
import { useFeatureEligibility } from '@modules/featureControl';

function set(store, { key, value }) {
  store.commit('extraStore/set', { scope: 'trueFit', key, value });
}

/**
   * @typedef {Object} tfParams
   * @property {Function} [fitrecProductSuccessCb]
   * @property {Function} [fitrecRegisterOpenCb]
   * @property {Function} [fitrecRegisterCompleteCb]
   * @property {Function} [fitrecHideSkeletonCb]
   * @example
   * import { useTrueFit } from '@composables/useTrueFit';
   * const { registerEventListeners } = useTrueFit();
   * registerEventListeners({
   *   fitrecProductSuccessCb: () => console.log('fitrecProductSuccessCb'),
   *   fitrecRegisterOpenCb: () => console.log('fitrecRegisterOpenCb'),
   *   fitrecRegisterCompleteCb: () => console.log('fitrecRegisterCompleteCb'),
   *   fitrecHideSkeletonCb: () => console.log('fitrecHideSkeletonCb'),
   * });
   * */

export const useTrueFit = () => {
  const store = useStore();

  const lastSelectedSize = computed({
    get() {
      return store.state.extraStore.trueFit.lastSelectedSize;
    },
    set(value) {
      set(store, { key: 'lastSelectedSize', value });
    },
  });

  /**
   *
   * @param {tfParams} param0
   * @returns {void}
   */
  const registerEventListeners = ({
    fitrecProductSuccessCb,
    fitrecRegisterOpenCb,
    fitrecRegisterCompleteCb,
    fitrecHideSkeletonCb,
  }) => {
    // @ts-ignore
    const { tfcapi } = window;

    if (!tfcapi) {
      throw new Error('tfcapi is not defined');
    }
    // These events are available when using the tfc-fitrec-product API on a PDP.
    tfcapi('event', 'tfc-fitrec-product', 'success', fitrecProductSuccessCb);
    tfcapi('event', 'tfc-fitrec-product', 'error', fitrecHideSkeletonCb);
    tfcapi('event', 'tfc-fitrec-product', 'render', fitrecHideSkeletonCb);
    // These events are available on any page where the user can launch the True Fit popup.
    tfcapi('event', 'tfc-fitrec-register', 'open', fitrecRegisterOpenCb);
    tfcapi('event', 'tfc-fitrec-register', 'complete', fitrecRegisterCompleteCb);
    tfcapi('event', 'tfc-fitrec-register', 'update', fitrecRegisterCompleteCb);
  };

  /**
   *
   * @param {tfParams} param0
   * @returns {Promise<void>}
   */
  const initTrueFit = async ({
    fitrecProductSuccessCb,
    fitrecRegisterOpenCb,
    fitrecRegisterCompleteCb,
    fitrecHideSkeletonCb,
  }) => {
    const { isTrueFitEligible } = useFeatureEligibility();

    if (!isTrueFitEligible) return;

    try {
      await loadTrueFit();
      registerEventListeners({
        fitrecProductSuccessCb,
        fitrecRegisterOpenCb,
        fitrecRegisterCompleteCb,
        fitrecHideSkeletonCb,
      });
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
      throw new Error(`loadTrueFit failed: ${e}`);
    }
  };

  return {
    lastSelectedSize,
    initTrueFit,
  };
};

export default useTrueFit;
